const MINUTE_MS = 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

const stationDateFormatterCache = new Map<string, Intl.DateTimeFormat>()
const stationOffsetFormatterCache = new Map<string, Intl.DateTimeFormat>()
const stationDateTimeFormatterCache = new Map<string, Intl.DateTimeFormat>()

export const formatOrdinal = (day: number): string => {
  const remainder = day % 100
  if (remainder >= 11 && remainder <= 13) {
    return `${day}th`
  }

  switch (day % 10) {
    case 1:
      return `${day}st`
    case 2:
      return `${day}nd`
    case 3:
      return `${day}rd`
    default:
      return `${day}th`
  }
}

export const resolveStationTimeZone = (timeZone?: string | null): string => {
  if (typeof timeZone === 'string') {
    const trimmed = timeZone.trim()
    if (trimmed.length > 0) {
      return trimmed
    }
  }

  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (resolved && resolved.trim().length > 0) {
      return resolved
    }
  } catch (error) {
    console.warn('Failed to resolve browser timezone, defaulting to UTC', error)
  }

  return 'UTC'
}

const getStationDateFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const key = `date:${timeZone}`
  const cached = stationDateFormatterCache.get(key)
  if (cached) {
    return cached
  }
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  stationDateFormatterCache.set(key, formatter)
  return formatter
}

const getStationOffsetFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const key = `offset:${timeZone}`
  const cached = stationOffsetFormatterCache.get(key)
  if (cached) {
    return cached
  }
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'shortOffset',
    })
  } catch (error) {
    console.warn('Falling back to short timezone names', error)
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    })
  }
  stationOffsetFormatterCache.set(key, formatter)
  return formatter
}

const getStationDateTimeFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const key = `datetime:${timeZone}`
  const cached = stationDateTimeFormatterCache.get(key)
  if (cached) {
    return cached
  }
  const formatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  stationDateTimeFormatterCache.set(key, formatter)
  return formatter
}

type StationDateParts = { year: number; month: number; day: number }

const getStationDateParts = (epochMs: number, timeZone: string): StationDateParts | null => {
  if (!Number.isFinite(epochMs)) {
    return null
  }
  try {
    const formatter = getStationDateFormatter(timeZone)
    const parts = formatter.formatToParts(epochMs)
    const year = Number(parts.find((part) => part.type === 'year')?.value ?? Number.NaN)
    const month = Number(parts.find((part) => part.type === 'month')?.value ?? Number.NaN)
    const day = Number(parts.find((part) => part.type === 'day')?.value ?? Number.NaN)
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      return null
    }
    return { year, month, day }
  } catch (error) {
    console.error('Failed to resolve station date parts', error)
    return null
  }
}

const parseOffsetMinutes = (value: string): number => {
  const normalized = value.toUpperCase()
  const match = normalized.match(/([+-])(\d{1,2})(?::?(\d{2}))?$/)
  if (!match) {
    return 0
  }
  const sign = match[1] === '-' ? -1 : 1
  const hours = Number.parseInt(match[2], 10)
  const minutes = match[3] ? Number.parseInt(match[3], 10) : 0
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return 0
  }
  return sign * (hours * 60 + minutes)
}

const getTimeZoneOffsetMs = (epochMs: number, timeZone: string): number => {
  try {
    const formatter = getStationOffsetFormatter(timeZone)
    const parts = formatter.formatToParts(epochMs)
    const offsetValue = parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
    const offsetMinutes = parseOffsetMinutes(offsetValue)
    return offsetMinutes * MINUTE_MS
  } catch (error) {
    console.error('Failed to resolve timezone offset', error)
    return 0
  }
}

export const formatStationTime = (
  epochMs: number,
  stationTz: string,
  opts: Intl.DateTimeFormatOptions,
): string => {
  const timeZone = resolveStationTimeZone(stationTz)
  return new Intl.DateTimeFormat(undefined, { timeZone, ...opts }).format(epochMs)
}

export const formatStationDateTime = (epochMs: number, stationTz?: string | null): string => {
  if (!Number.isFinite(epochMs)) {
    return ''
  }
  const timeZone = resolveStationTimeZone(stationTz)
  const formatter = getStationDateTimeFormatter(timeZone)
  const formatted = formatter.format(epochMs)
  return formatted.replace('T', ' ')
}

export const stationDayDiff = (
  aEpochMs: number,
  bEpochMs: number,
  stationTz?: string | null,
): number => {
  if (!Number.isFinite(aEpochMs) || !Number.isFinite(bEpochMs)) {
    return 0
  }
  const timeZone = resolveStationTimeZone(stationTz)
  const aParts = getStationDateParts(aEpochMs, timeZone)
  const bParts = getStationDateParts(bEpochMs, timeZone)
  if (!aParts || !bParts) {
    return 0
  }
  const aMidnightUtc = Date.UTC(aParts.year, aParts.month - 1, aParts.day)
  const bMidnightUtc = Date.UTC(bParts.year, bParts.month - 1, bParts.day)
  return Math.round((aMidnightUtc - bMidnightUtc) / DAY_MS)
}

export const isSameStationDay = (
  aEpoch: number,
  bEpoch: number,
  stationTz: string,
): boolean => stationDayDiff(aEpoch, bEpoch, stationTz) === 0

const parseIsoToEpochMs = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed.includes('T')) {
    return null
  }
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return null
  }
  const parsed = Date.parse(trimmed)
  return Number.isNaN(parsed) ? null : parsed
}

export interface EpochSource {
  epochMs?: number | null | undefined
  epochSeconds?: number | null | undefined
  isoString?: string | null | undefined
}

export const resolveEpochMs = (source: EpochSource | number | null | undefined): number | null => {
  if (source === null || source === undefined) {
    return null
  }
  if (typeof source === 'number') {
    if (!Number.isFinite(source)) {
      return null
    }
    return source > 1_000_000_000_000 ? Math.trunc(source) : Math.trunc(source * 1000)
  }
  if (typeof source.epochMs === 'number' && Number.isFinite(source.epochMs)) {
    return Math.trunc(source.epochMs)
  }
  if (typeof source.epochSeconds === 'number' && Number.isFinite(source.epochSeconds)) {
    return Math.trunc(source.epochSeconds * 1000)
  }
  if (typeof source.isoString === 'string') {
    const parsed = parseIsoToEpochMs(source.isoString)
    if (parsed !== null) {
      return parsed
    }
  }
  return null
}

const parseStationLocalDateTime = (value: string): {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
} | null => {
  const normalized = value.replace('T', ' ').trim()
  if (!normalized) {
    return null
  }
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/,
  )
  if (!match) {
    return null
  }
  const [_, year, month, day, hour, minute, second] = match
  const parsedYear = Number.parseInt(year, 10)
  const parsedMonth = Number.parseInt(month, 10)
  const parsedDay = Number.parseInt(day, 10)
  const parsedHour = Number.parseInt(hour, 10)
  const parsedMinute = Number.parseInt(minute, 10)
  const parsedSecond = Number.parseInt(second ?? '0', 10)
  if (
    [parsedYear, parsedMonth, parsedDay, parsedHour, parsedMinute, parsedSecond].some(
      (part) => !Number.isFinite(part),
    )
  ) {
    return null
  }
  return {
    year: parsedYear,
    month: parsedMonth,
    day: parsedDay,
    hour: parsedHour,
    minute: parsedMinute,
    second: parsedSecond,
  }
}

export const stationLocalDateTimeToEpochMs = (
  value: string | null | undefined,
  stationTz?: string | null,
): number | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }
  const isoParsed = parseIsoToEpochMs(trimmed)
  if (isoParsed !== null) {
    return isoParsed
  }
  const parts = parseStationLocalDateTime(trimmed)
  if (!parts) {
    return null
  }
  const timeZone = resolveStationTimeZone(stationTz)
  const baseUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  )
  if (!Number.isFinite(baseUtcMs)) {
    return null
  }
  let guess = baseUtcMs
  for (let i = 0; i < 4; i += 1) {
    const offsetMs = getTimeZoneOffsetMs(guess, timeZone)
    const candidate = baseUtcMs - offsetMs
    if (Math.abs(candidate - guess) < 1) {
      return candidate
    }
    guess = candidate
  }
  return guess
}

export const formatMessageTimestamp = (
  epochMs: number | null | undefined,
  stationTz?: string | null,
  nowEpochMs: number = Date.now(),
): string => {
  if (!Number.isFinite(epochMs ?? Number.NaN)) {
    return '—'
  }
  const timestamp = Math.trunc(Number(epochMs))
  const timeZone = resolveStationTimeZone(stationTz)
  const diffMs = nowEpochMs - timestamp
  if (diffMs >= 0) {
    const diffMinutes = Math.max(1, Math.floor(diffMs / MINUTE_MS))
    if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`
    }
  }
  const timeLabel = formatStationTime(timestamp, timeZone, { hour: 'numeric', minute: '2-digit' })
  if (isSameStationDay(timestamp, nowEpochMs, timeZone)) {
    return `${timeLabel} · Today`
  }
  const dayDifference = stationDayDiff(nowEpochMs, timestamp, timeZone)
  if (dayDifference === 1) {
    return `${timeLabel} · Yesterday`
  }
  const weekday = formatStationTime(timestamp, timeZone, { weekday: 'long' })
  const targetParts = getStationDateParts(timestamp, timeZone)
  const nowParts = getStationDateParts(nowEpochMs, timeZone)
  if (!targetParts || !nowParts) {
    const dateLabel = formatStationTime(timestamp, timeZone, { dateStyle: 'medium' })
    return `${timeLabel} · ${dateLabel}`
  }
  const dayOrdinal = formatOrdinal(targetParts.day)
  const monthName = formatStationTime(timestamp, timeZone, { month: 'long' })
  if (targetParts.year === nowParts.year) {
    if (targetParts.month === nowParts.month) {
      return `${timeLabel} · ${weekday} ${dayOrdinal}`
    }
    return `${timeLabel} · ${weekday} ${dayOrdinal} ${monthName}`
  }
  return `${timeLabel} · ${weekday} ${dayOrdinal} ${monthName} ${targetParts.year}`
}
